import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authApi, settingsApi, type TenantSettings } from '@/lib/api';
import { getSubdomain } from '@/lib/subdomain';

interface TenantBranding {
  tenant_id: string;
  name: string;
  subdomain: string;
  logo_url: string | null;
  settings: Record<string, unknown>;
}

interface TenantContextType {
  branding: TenantBranding | null;
  isLoading: boolean;
  error: string | null;
  updateSettings: (settings: TenantSettings) => Promise<boolean>;
  refreshBranding: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | null>(null);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBranding = useCallback(async () => {
    const subdomain = getSubdomain();

    if (!subdomain) {
      setError('No tenant detected. Please access via a valid subdomain.');
      setIsLoading(false);
      return;
    }

    const result = await authApi.branding();

    if (result.success && result.data) {
      setBranding(result.data);
      // Update page title
      document.title = `${result.data.name} - FieldForce`;
    } else {
      setError(result.error?.message || 'Failed to load tenant information');
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const updateSettings = useCallback(async (settings: TenantSettings): Promise<boolean> => {
    const result = await settingsApi.save(settings);
    if (result.success) {
      // Refresh branding to get updated settings
      await fetchBranding();
      return true;
    }
    return false;
  }, [fetchBranding]);

  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  return (
    <TenantContext.Provider value={{ branding, isLoading, error, updateSettings, refreshBranding }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
